import type { FreeformCheckTemplate } from "@/types/aicheck";

export const FREEFORM_TEMPLATE_STORAGE_KEY =
  "aicheck:freeform-template-rules:v2";

export interface FreeformTemplateRuleOverride {
  templateId: string;
  stage: string;
  description: string;
  prompt: string;
  deleted?: false;
  custom?: boolean;
  updatedAt: string;
}

export interface FreeformTemplateDeletedOverride {
  templateId: string;
  deleted: true;
  updatedAt: string;
}

export type FreeformTemplateOverride =
  | FreeformTemplateRuleOverride
  | FreeformTemplateDeletedOverride;

export interface FreeformTemplateExportBundle {
  version: 2;
  exportedAt: string;
  templates: FreeformTemplateOverride[];
}

const OVERRIDE_KEYS = new Set([
  "templateId",
  "stage",
  "description",
  "prompt",
  "deleted",
  "custom",
  "updatedAt",
]);

function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

function validText(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function normalizeTemplateId(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeOverride(
  value: unknown,
  index: number,
): FreeformTemplateOverride {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`Invalid Freeform Review rule at index ${index}.`);
  }
  const item = value as Record<string, unknown>;
  for (const key of Object.keys(item)) {
    if (!OVERRIDE_KEYS.has(key)) {
      throw new Error(
        `Invalid Freeform Review rule at index ${index}: unsupported field "${key}".`,
      );
    }
  }
  const templateId = normalizeTemplateId(item.templateId);
  if (!templateId) {
    throw new Error(`Invalid Freeform Review rule at index ${index}.`);
  }
  if (!validText(item.updatedAt)) {
    throw new Error(
      `Invalid Freeform Review rule at index ${index}: updatedAt is required.`,
    );
  }

  const deleted = item.deleted === true;
  if (deleted) {
    return {
      templateId,
      deleted: true,
      updatedAt: item.updatedAt,
    };
  }

  if (
    !validText(item.stage) ||
    !validText(item.description) ||
    !validText(item.prompt)
  ) {
    throw new Error(`Invalid Freeform Review rule at index ${index}.`);
  }

  return {
    templateId,
    stage: item.stage,
    description: item.description,
    prompt: item.prompt,
    ...(item.custom === true ? { custom: true } : undefined),
    updatedAt: item.updatedAt,
  };
}

function assertTemplateRule(
  template: FreeformCheckTemplate,
  prompt = template.defaultPrompt,
): void {
  if (!validText(template.id)) {
    throw new Error("Freeform Review rule id is required.");
  }
  if (!validText(template.stage)) {
    throw new Error("Freeform Review rule stage is required.");
  }
  if (!validText(template.description)) {
    throw new Error("Freeform Review rule description is required.");
  }
  if (!validText(prompt)) {
    throw new Error("Freeform Review rule prompt is required.");
  }
}

export function parseFreeformTemplateBundle(
  value: unknown,
): FreeformTemplateExportBundle {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Import file must be a JSON object.");
  }
  const bundle = value as Record<string, unknown>;
  if (bundle.version !== 2) {
    throw new Error("Unsupported Freeform Review template bundle version.");
  }
  if (!Array.isArray(bundle.templates)) {
    throw new Error("Import file must include a templates array.");
  }
  if (!validText(bundle.exportedAt)) {
    throw new Error("Import file must include exportedAt.");
  }
  return {
    version: 2,
    exportedAt: bundle.exportedAt,
    templates: bundle.templates.map(normalizeOverride),
  };
}

export function readFreeformTemplateOverrides(): Record<
  string,
  FreeformTemplateOverride
> {
  if (!canUseLocalStorage()) return {};
  try {
    const raw = localStorage.getItem(FREEFORM_TEMPLATE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    const bundle = parseFreeformTemplateBundle(parsed);
    return Object.fromEntries(
      bundle.templates.map((override) => [override.templateId, override]),
    );
  } catch {
    return {};
  }
}

export function writeFreeformTemplateOverrides(
  overrides: Record<string, FreeformTemplateOverride>,
): void {
  if (!canUseLocalStorage()) return;
  const bundle: FreeformTemplateExportBundle = {
    version: 2,
    exportedAt: new Date().toISOString(),
    templates: Object.values(overrides).sort((a, b) =>
      a.templateId.localeCompare(b.templateId),
    ),
  };
  localStorage.setItem(FREEFORM_TEMPLATE_STORAGE_KEY, JSON.stringify(bundle));
}

export function applyFreeformTemplateOverrides(
  standardTemplates: FreeformCheckTemplate[],
  overrides = readFreeformTemplateOverrides(),
): FreeformCheckTemplate[] {
  const standardIds = new Set(standardTemplates.map((template) => template.id));
  const merged = standardTemplates
    .map((template) => {
      const override = overrides[template.id];
      if (override?.deleted) return null;
      if (!override) return template;
      return {
        ...template,
        stage: override.stage,
        description: override.description,
        defaultPrompt: override.prompt,
      };
    })
    .filter((template): template is FreeformCheckTemplate => Boolean(template));

  for (const override of Object.values(overrides)) {
    if (override.deleted || standardIds.has(override.templateId)) continue;
    merged.push({
      id: override.templateId,
      stage: override.stage,
      description: override.description,
      defaultPrompt: override.prompt,
      dataPolicy: "standard",
    });
  }

  return merged;
}

export function templateOverrideFromTemplate(
  template: FreeformCheckTemplate,
  prompt = template.defaultPrompt,
  custom = false,
): FreeformTemplateRuleOverride {
  assertTemplateRule(template, prompt);
  return {
    templateId: template.id.trim(),
    stage: template.stage.trim(),
    description: template.description.trim(),
    prompt,
    ...(custom ? { custom: true } : undefined),
    updatedAt: new Date().toISOString(),
  };
}

export function saveFreeformTemplateOverride(
  template: FreeformCheckTemplate,
  prompt: string,
  custom = false,
): FreeformTemplateOverride {
  const override = templateOverrideFromTemplate(template, prompt, custom);
  const overrides = readFreeformTemplateOverrides();
  writeFreeformTemplateOverrides({
    ...overrides,
    [template.id]: override,
  });
  return override;
}

export function deleteFreeformTemplateRule(
  templateId: string,
  isStandardTemplate: boolean,
): void {
  const overrides = readFreeformTemplateOverrides();
  if (!isStandardTemplate) {
    delete overrides[templateId];
  } else {
    overrides[templateId] = {
      templateId,
      deleted: true,
      updatedAt: new Date().toISOString(),
    };
  }
  writeFreeformTemplateOverrides(overrides);
}

export function createFreeformTemplateRule(
  template: FreeformCheckTemplate,
): FreeformTemplateOverride {
  return saveFreeformTemplateOverride(template, template.defaultPrompt, true);
}

export function restoreFreeformTemplateDefaults(
  templateIds: string[],
  standardTemplates: FreeformCheckTemplate[],
): number {
  const standardIds = new Set(standardTemplates.map((template) => template.id));
  const overrides = readFreeformTemplateOverrides();
  let restored = 0;
  for (const templateId of templateIds) {
    if (!standardIds.has(templateId) || !overrides[templateId]) continue;
    delete overrides[templateId];
    restored += 1;
  }
  writeFreeformTemplateOverrides(overrides);
  return restored;
}

export function createFreeformTemplateExportBundle(
  templates: FreeformCheckTemplate[],
  templateIds = templates.map((template) => template.id),
): FreeformTemplateExportBundle {
  const selectedIds = new Set(templateIds);
  const templateIdsInOrder = templates.map((template) => template.id);
  for (const templateId of selectedIds) {
    if (!templateIdsInOrder.includes(templateId)) {
      throw new Error(`Unknown Freeform Review rule selected: ${templateId}`);
    }
  }
  return {
    version: 2,
    exportedAt: new Date().toISOString(),
    templates: templates
      .filter((template) => selectedIds.has(template.id))
      .map((template) => templateOverrideFromTemplate(template))
      .sort((a, b) => a.templateId.localeCompare(b.templateId)),
  };
}

export function importFreeformTemplateBundle(
  bundle: FreeformTemplateExportBundle,
  templateIds = bundle.templates.map((template) => template.templateId),
): number {
  const selectedIds = new Set(templateIds);
  const importableIds = new Set(
    bundle.templates.map((template) => template.templateId),
  );
  for (const templateId of selectedIds) {
    if (!importableIds.has(templateId)) {
      throw new Error(`Unknown Freeform Review rule selected: ${templateId}`);
    }
  }
  const current = readFreeformTemplateOverrides();
  const next = { ...current };
  let imported = 0;
  for (const item of bundle.templates) {
    if (!selectedIds.has(item.templateId) || item.deleted) continue;
    next[item.templateId] = {
      ...item,
      custom: true,
      updatedAt: new Date().toISOString(),
    };
    imported += 1;
  }
  writeFreeformTemplateOverrides(next);
  return imported;
}
